package ai.wagemix.logic.jobdescriptions

import ai.wagemix.net.models.firebase.WMSubscriptionType
import co.touchlab.kermit.Severity
import com.fsryan.foundation.util.log.FSLog
import com.fsryan.foundation.util.log.sendEventInclusive

/*
 * The analytics events of the job descriptions presenter (planning/analytics-plan.md §5, "Job
 * descriptions"). Re-evaluation events are shared with the mix builder and live in
 * FSLogWMStructuredEvents.kt. The file name and the URL are never sent.
 */

/** What the user handed in (`job_description_upload_started.source`). */
internal enum class WMJobDescriptionSource { File, Url }

/** Why the pipeline itself failed (`job_description_failed.reason`, beside [WMFileProblem] and [WMUrlProblem]). */
internal enum class WMJobDescriptionPipelineFailure { QuotaExceeded, PipelineFailed }

private inline fun FSLog.sendWMJobDescriptionsEvent(
    eventName: String,
    throwable: Throwable? = null,
    crossinline fields: MutableMap<String, Any>.() -> Unit = {}
) {
    sendEventInclusive(throwable = throwable, severity = Severity.Info, eventName = eventName) {
        fields()
    }
}

/** [fileType] is the file's lower-case extension (files only); [uploadQuota] is `null` for an unlimited plan. */
internal fun FSLog.sendWMJobDescriptionUploadStarted(
    source: WMJobDescriptionSource,
    fileType: String?,
    uploadsUsed: Int,
    uploadQuota: Int?,
) {
    sendWMJobDescriptionsEvent(eventName = "WMJobDescriptionUploadStarted") {
        put(
            "source",
            when (source) {
                WMJobDescriptionSource.File -> "file"
                WMJobDescriptionSource.Url -> "url"
            }
        )
        fileType?.let { put("file_type", it) }
        put("uploads_used", uploadsUsed)
        uploadQuota?.let { put("upload_quota", it) }
    }
}

/** [occupationCount] is omitted while the produced mix has not reached the mirror yet. */
internal fun FSLog.sendWMJobDescriptionAnalyzed(mixId: String, durationMs: Long?, occupationCount: Int?) {
    sendWMJobDescriptionsEvent(eventName = "WMJobDescriptionAnalyzed") {
        durationMs?.let { put("duration_ms", it) }
        occupationCount?.let { put("occupation_count", it) }
        put("mix_id", mixId)
    }
}

/** [reason] is a [WMFileProblem], a [WMUrlProblem] or a [WMJobDescriptionPipelineFailure]; its name is sent. */
internal fun FSLog.sendWMJobDescriptionFailed(reason: Enum<*>, error: Throwable? = null) {
    sendWMJobDescriptionsEvent(eventName = "WMJobDescriptionFailed", throwable = error) {
        put("reason", reason.name)
    }
}

internal fun FSLog.sendWMUploadQuotaReached(plan: WMSubscriptionType) {
    sendWMJobDescriptionsEvent(eventName = "WMUploadQuotaReached") {
        put("plan", plan.stringValue)
    }
}
